import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import {
  ActionPanelComponentContext,
  CombinedEvent,
  EventType,
} from "@redotech/redo-merchant-app-common/action-panel";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomerEvents } from "@redotech/redo-merchant-app-common/client/customer";
import { getTeamReturns } from "@redotech/redo-merchant-app-common/client/team";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  StorefrontClientContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import {
  ExpandedConversation,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { CustomerActivityFilterOptions } from "@redotech/redo-model/customer-activity/customer-activity-filters";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { Flex } from "@redotech/redo-web/flex";
import { Tabs, TabSize } from "@redotech/redo-web/tab";
import { getCart } from "@redotech/shopify-storefront";
import { memo, useContext, useEffect, useState } from "react";
import { getConversationTimeline } from "../client/conversations";
import { ConversationDetailsPanel } from "../conversation-detail-panel/conversation-details-panel";

enum ContextTab {
  DETAILS = "details",
  ACTIVITY = "conversation-activity",
}

const contextTabLabel = {
  [ContextTab.DETAILS]: "Details",
  [ContextTab.ACTIVITY]: "Timeline",
};

export const ConversationContext = memo(function ConversationContext({
  conversation,
  setConversationAssignee,
  setActiveConversation,
}: {
  conversation: MinimalConversation | ExpandedConversation;
  setConversationAssignee: (assignee: any) => void;
  setActiveConversation: (conversation: MinimalConversation) => void;
}) {
  const { ActivityPanel } = useContext(ActionPanelComponentContext);

  const team = useRequiredContext(TeamContext);
  const storefrontClient = useContext(StorefrontClientContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const client = useRequiredContext(RedoMerchantClientContext);

  const [tab, setTab] = useState<ContextTab>(ContextTab.DETAILS);

  const primaryEmail = getPrimaryCustomerEmail(conversation.customer);

  const viewReturns = useHasPermission(
    SupportPermissions.VIEW_CUSTOMER_RETURNS,
  );
  const viewOrders = useHasPermission(SupportPermissions.VIEW_CUSTOMER_ORDERS);

  const [returnsLoad, doReturnsLoad] = useTriggerLoad(async (signal) => {
    if (!viewReturns) {
      return [];
    }
    if (primaryEmail) {
      return await getTeamReturns(client, { email: primaryEmail, signal });
    }
  });

  const [cartsLoad, doCartsLoad] = useTriggerLoad(async (signal) => {
    if (storefrontClient && conversation.carts?.length) {
      const cartPromises = conversation.carts.map(async (cartId: string) => {
        return await getCart(storefrontClient, `gid://shopify/Cart/${cartId}`);
      });
      return await Promise.all(cartPromises);
    }
    return [];
  });

  const [ordersLoad, doOrdersLoad] = useTriggerLoad(async (signal) => {
    if (!viewOrders) {
      return [];
    }
    if (primaryEmail) {
      const result = await rpcClient.getTeamOrdersByEmail({
        email: primaryEmail,
      });
      return result.orders;
    }
    return [];
  });

  const [customerActivityFilterOptions, setCustomerActivityFilterOptions] =
    useState<CustomerActivityFilterOptions | undefined>(undefined);

  const conversationActivityLoad = useLoad(
    async (signal): Promise<CombinedEvent[]> => {
      const conversationActivity = await getConversationTimeline(client, {
        conversationId: conversation._id,
        signal,
      });
      const typedConversationActivity: CombinedEvent[] =
        conversationActivity.map((entry) => ({
          event: { ...entry, timestamp: new Date(entry.timestamp) },
          type: EventType.CONVERSATION,
        }));
      let typedCustomerActivity: CombinedEvent[] = [];
      if (conversation.customer?.customer) {
        const customerActivity = await getCustomerEvents(client, {
          customerId: conversation.customer.customer,
          filterOptions: customerActivityFilterOptions,
          signal,
        });
        typedCustomerActivity = customerActivity.customerEvents.map(
          (event) => ({ event, type: EventType.CUSTOMER }),
        );
      }

      const combinedActivity = [
        ...typedCustomerActivity,
        ...typedConversationActivity,
      ];
      return combinedActivity.sort(
        (a, b) => b.event.timestamp.getTime() - a.event.timestamp.getTime(),
      );
    },
    [
      conversation._id,
      customerActivityFilterOptions,
      conversation.customer?.customer,
    ],
  );

  useEffect(() => {
    doOrdersLoad();
    doReturnsLoad();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation._id, conversation?.customer?.customer]);

  useEffect(() => {
    doCartsLoad();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storefrontClient, conversation]);

  const availableTabs = team.settings.customerAccounts?.enabled
    ? Object.values(ContextTab)
    : [ContextTab.DETAILS, ContextTab.ACTIVITY];

  return (
    <Tabs<ContextTab>
      customContainerCss={{ flexShrink: 0 }}
      customOutletCss={{ paddingTop: "0" }}
      customTabCss={{ fontSize: "16px", height: "40px" }}
      keyFn={(option) => option}
      options={availableTabs}
      tab={(option) => contextTabLabel[option]}
      tabSize={TabSize.SMALL}
      value={tab}
      valueChange={setTab}
    >
      {tab === ContextTab.DETAILS && (
        <ConversationDetailsPanel
          cartsLoad={cartsLoad}
          conversation={conversation}
          doCartsLoad={doCartsLoad}
          doOrdersLoad={doOrdersLoad}
          doReturnsLoad={doReturnsLoad}
          onInsightsUpdated={(conversationInsights) => {
            setActiveConversation({ ...conversation, conversationInsights });
          }}
          ordersLoad={ordersLoad}
          returnsLoad={returnsLoad}
          setActiveConversation={setActiveConversation}
          setConversationAssignee={setConversationAssignee}
        />
      )}
      {tab === ContextTab.ACTIVITY && (
        <Flex dir="column" w="full">
          <ActivityPanel
            conversationActivityLoad={conversationActivityLoad}
            customerActivityFilterOptions={customerActivityFilterOptions}
            setCustomerActivityFilterOptions={setCustomerActivityFilterOptions}
          />
        </Flex>
      )}
    </Tabs>
  );
});
